import React, { SyntheticEvent, useContext, useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Badge from '@mui/material/Badge'
import { useLocation, useNavigate } from 'react-router-dom'
import { Autocomplete, MenuItem, styled, TextField } from '@mui/material'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import FixedWidthContainer from 'components/FixedWidthContainer'
import { CartContext } from 'contexts/CartContext'
import { Product } from 'hooks/useProduct'
import { AuthContext } from 'contexts/AuthContext'
import { WIX_HOME_URL } from 'config'
import useSearchProducts from 'hooks/useSearchProducts'
import { useTranslation } from 'react-i18next'
import LanguageIcon from '@mui/icons-material/Language'

const StyledBadge = styled(Badge)(() => ({
  '& .MuiBadge-badge': {
    color: 'white',
    background: '#A11E20'
  }
}))

const CustomListItemButton = styled(ListItemButton)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: '10px 16px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(161, 30, 32, 0.08)'
  },
  '& .MuiListItemText-primary': {
    color: '#333',
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 400,
    textAlign: 'center'
  },
  '&:hover .MuiListItemText-primary': {
    color: theme.palette.primary.main
  }
}))

const Navbar = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { getCartDetails } = useContext(CartContext)
  const { cartItemCount, totalPrice } = getCartDetails()
  const { isAuthenticated } = useContext(AuthContext)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const { products: suggestions } = useSearchProducts({
    keyword: searchKeyword,
    limit: 5
  })
  const { t, i18n } = useTranslation()

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng)
  }

  const pages = [
    t('navbar.products'),
    t('navbar.quickOrder'),
    t('navbar.contactUs'),
    t('navbar.download')
  ]

  const handleSearchChange = (
    event: SyntheticEvent<Element, Event>,
    value: string
  ) => {
    setSearchKeyword(value)
    setIsDropdownOpen(!!value.trim())
  }

  const handleSuggestionSelect = (
    event: SyntheticEvent<Element, Event>,
    product: Product | null
  ) => {
    if (product) {
      navigate(`/products/${product.id}`)
      setSearchKeyword('')
      setIsDropdownOpen(false)
    }
  }

  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Enter') {
      navigateToSearch()
      setIsDropdownOpen(false)
      setSearchKeyword('')
    }
  }

  const navigateToSearch = () => {
    if (searchKeyword.trim()) {
      navigate(`/search?keyword=${encodeURIComponent(searchKeyword.trim())}`)
      setIsDropdownOpen(false)
    }
  }

  const pageToPathMap: { [key: string]: string } = {
    [t('navbar.products')]: '/our-products',
    [t('navbar.quickOrder')]: '/quick-order',
    [t('navbar.contactUs')]: '/contact-us',
    [t('navbar.download')]: '/download'
  }

  const [selectedPage, setSelectedPage] = useState('')

  const handleNavigate = (page: string) => {
    if (page === t('navbar.products')) {
      navigate('/our-products?category=Cabinets')
    } else if (page === 'Home') {
      navigate('/')
    } else {
      const path = pageToPathMap[page]
      if (path) {
        navigate(path)
      }
    }
  }

  const handleSubmenuNavigate = (category: string) => {
    navigate(`/our-products?category=${encodeURIComponent(category)}`)
  }

  useEffect(() => {
    const pathToPageMap: { [key: string]: string } = {
      '/our-products': t('navbar.products'),
      '/quick-order': t('navbar.quickOrder'),
      '/contact-us': t('navbar.contactUs'),
      '/download': t('navbar.download')
    }
    const currentPath = location.pathname.split('/')[1]
      ? `/${location.pathname.split('/')[1]}`
      : '/'
    const mappedPage = pathToPageMap[currentPath] || ''
    setSelectedPage(mappedPage)
  }, [location.pathname, t])

  return (
    <Box
      sx={{
        height: '90px',
        bgcolor: '#FDFDFD'
      }}
    >
      <FixedWidthContainer
        sx={{
          height: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box
          sx={{
            gap: '30px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Box
            component='img'
            src='/logo.jpg'
            alt='logo'
            sx={{
              cursor: 'pointer',
              width: '181px',
              height: '27px'
            }}
            onClick={() => {
              window.location.assign(WIX_HOME_URL)
            }}
          />

          <Box sx={{ flexGrow: 1, display: 'flex' }}>
            {pages.map(page => (
              <Box
                key={page}
                className={`menu-item ${
                  selectedPage === page ? 'selected' : ''
                }`}
                sx={{
                  position: 'relative',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  '& .submenu': {
                    p: 0,
                    opacity: 0,
                    visibility: 'hidden',
                    transform: 'translateY(-10px)',
                    transition: 'all 0.2s ease',
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: '50%',
                    marginLeft: '-60px',
                    bgcolor: 'white',
                    zIndex: 1,
                    minWidth: '120px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
                    border: '1px solid #E0E0E0',
                    overflow: 'hidden'
                  },
                  '&:hover .submenu': {
                    opacity: 1,
                    visibility: 'visible',
                    transform: 'translateY(0)'
                  }
                }}
              >
                <Typography
                  className={`menu-item-text ${
                    selectedPage === page ? 'selected' : ''
                  }`}
                  sx={{
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    fontWeight: selectedPage === page ? 700 : 400,
                    lineHeight: '16.94px',
                    color: selectedPage === page ? 'primary.main' : '#000000',
                    textAlign: 'center',
                    padding: '10px',
                    cursor: 'pointer',
                    position: 'relative',
                    pb: 0
                  }}
                  onClick={() => handleNavigate(page)}
                >
                  {page}
                </Typography>

                {page === t('navbar.products') && (
                  <List className='submenu' disablePadding>
                    <CustomListItemButton
                      onClick={() => handleSubmenuNavigate('Cabinets')}
                    >
                      <ListItemText primary={t('categories.cabinets')} />
                    </CustomListItemButton>
                    <CustomListItemButton
                      onClick={() => handleSubmenuNavigate('Hardware')}
                    >
                      <ListItemText primary={t('categories.hardware')} />
                    </CustomListItemButton>
                    <CustomListItemButton
                      onClick={() => handleSubmenuNavigate('Richelieu')}
                    >
                      <ListItemText primary={t('categories.richelieu')} />
                    </CustomListItemButton>
                  </List>
                )}
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            gap: '30px',
            display: 'flex',
            alignItems: 'center',
            position: 'relative'
          }}
        >
          {isAuthenticated && (
            <>
              <Box
                sx={{
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative',
                  flexGrow: 1,
                  maxWidth: '300px',
                  '&:focus-within .suggestions-list': {
                    display: 'block'
                  }
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#F8F8F8',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '5px 10px',
                    borderRadius: '20px',
                    width: '150px',
                    boxSizing: 'border-box'
                  }}
                >
                  <Autocomplete
                    freeSolo={false}
                    options={suggestions || []}
                    getOptionLabel={(option: Product) =>
                      option?.sku?.toLowerCase?.() || ''
                    }
                    onInputChange={handleSearchChange}
                    onChange={handleSuggestionSelect}
                    inputValue={searchKeyword}
                    renderOption={(
                      props: React.HTMLAttributes<HTMLLIElement>,
                      option: Product
                    ) => (
                      <MenuItem
                        key={option.id}
                        {...props}
                        onClick={() => {
                          navigate(`/products/${option.id}`)
                          setSearchKeyword('')
                          setIsDropdownOpen(false)
                        }}
                      >
                        {option.sku}
                      </MenuItem>
                    )}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder={t('navbar.search')}
                        variant='standard'
                        InputProps={{
                          ...params.InputProps,
                          disableUnderline: true,
                          startAdornment: (
                            <Box
                              component='img'
                              src='/magnifying_glass_icon.svg'
                              alt='search icon'
                              sx={{
                                width: '17.19px',
                                height: '17.19px',
                                cursor: 'pointer',
                                marginRight: '10px'
                              }}
                              onClick={navigateToSearch}
                            />
                          ),
                          endAdornment: null
                        }}
                        onKeyDown={handleSearchKeyDown}
                        sx={{
                          width: '100%',
                          fontFamily: 'Inter',
                          fontSize: '14px',
                          fontWeight: 400,
                          lineHeight: '16.94px',
                          backgroundColor: 'transparent'
                        }}
                      />
                    )}
                    disableClearable
                    open={isDropdownOpen}
                    onClose={() => setIsDropdownOpen(false)}
                    sx={{
                      width: '100%'
                    }}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px'
                }}
              >
                <StyledBadge
                  onClick={() => {
                    navigate('/quotes')
                  }}
                  sx={{ cursor: 'pointer' }}
                >
                  <Typography
                    variant='body2'
                    sx={{
                      height: '25px'
                    }}
                  >
                    {t('navbar.quote')}
                  </Typography>
                </StyledBadge>

                <StyledBadge
                  badgeContent={cartItemCount}
                  onClick={() => {
                    navigate('/cart')
                  }}
                  sx={{ cursor: 'pointer' }}
                >
                  <Box
                    component='img'
                    src='/cart_icon.svg'
                    alt='cart icon'
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '25px'
                    }}
                  />
                </StyledBadge>

                <Typography
                  variant='h4'
                  sx={{
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    width: '70px',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    navigate('/cart')
                  }}
                >
                  ${totalPrice}
                </Typography>
              </Box>
            </>
          )}

          {/* Language Selector */}
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              ml: isAuthenticated ? 2 : 0,
              '&:hover .language-dropdown': {
                opacity: 1,
                visibility: 'visible',
                transform: 'translateY(0)'
              }
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid #E0E0E0',
                backgroundColor: '#FAFAFA',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#F0F0F0',
                  borderColor: '#D0D0D0'
                }
              }}
            >
              <LanguageIcon
                sx={{
                  color: '#666',
                  fontSize: '20px'
                }}
              />
              <Typography
                variant='body2'
                sx={{
                  color: '#333',
                  fontWeight: 500,
                  fontSize: '13px'
                }}
              >
                {i18n.language === 'es' ? 'ES' : 'EN'}
              </Typography>
            </Box>

            <Box
              className='language-dropdown'
              sx={{
                opacity: 0,
                visibility: 'hidden',
                transform: 'translateY(-10px)',
                transition: 'all 0.2s ease',
                position: 'absolute',
                top: 'calc(100% + 4px)',
                right: 0,
                bgcolor: 'white',
                zIndex: 10,
                borderRadius: '8px',
                minWidth: '180px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
                overflow: 'hidden',
                border: '1px solid #E0E0E0',
                '&:hover': {
                  opacity: 1,
                  visibility: 'visible',
                  transform: 'translateY(0)'
                }
              }}
            >
              <Box
                onClick={() => changeLanguage('en')}
                sx={{
                  padding: '14px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  color: i18n.language === 'en' ? 'primary.main' : '#333',
                  fontWeight: i18n.language === 'en' ? 600 : 400,
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor:
                    i18n.language === 'en'
                      ? 'rgba(161, 30, 32, 0.08)'
                      : 'transparent',
                  borderLeft:
                    i18n.language === 'en'
                      ? '3px solid'
                      : '3px solid transparent',
                  borderLeftColor:
                    i18n.language === 'en' ? 'primary.main' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(161, 30, 32, 0.08)',
                    color: 'primary.main'
                  }
                }}
              >
                <Box component='span' sx={{ fontSize: '18px', mr: 1.5 }}>
                  🇺🇸
                </Box>
                <Box component='span'>{t('language.english')}</Box>
              </Box>
              <Box
                onClick={() => changeLanguage('es')}
                sx={{
                  padding: '14px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  color: i18n.language === 'es' ? 'primary.main' : '#333',
                  fontWeight: i18n.language === 'es' ? 600 : 400,
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor:
                    i18n.language === 'es'
                      ? 'rgba(161, 30, 32, 0.08)'
                      : 'transparent',
                  borderLeft:
                    i18n.language === 'es'
                      ? '3px solid'
                      : '3px solid transparent',
                  borderLeftColor:
                    i18n.language === 'es' ? 'primary.main' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(161, 30, 32, 0.08)',
                    color: 'primary.main'
                  }
                }}
              >
                <Box component='span' sx={{ fontSize: '18px', mr: 1.5 }}>
                  🇪🇸
                </Box>
                <Box component='span'>{t('language.spanish')}</Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </FixedWidthContainer>
    </Box>
  )
}

export default Navbar
